import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Database, FileUp, GitBranch, CheckCircle2, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { Case } from '../types';
import Button from '../components/ui/Button';
import Card, { CardHeader, CardBody } from '../components/ui/Card';
import Spinner from '../components/ui/Spinner';
import SchemaDesignStage from '../components/processing/SchemaDesignStage';
import DataFilesStage from '../components/processing/DataFilesStage';
import MappingVisualization from '../components/processing/MappingVisualization';

type ProcessingStage = 'schema_design' | 'data_files' | 'data_mapping' | 'completed';

export default function ProcessingPhase() {
  const navigate = useNavigate();
  const { caseId } = useParams<{ caseId: string }>();
  const [currentStage, setCurrentStage] = useState<ProcessingStage>('schema_design');

  // Fetch case data
  const { data: caseData, isLoading: isLoadingCase } = useQuery<Case>({
    queryKey: ['case', caseId],
    queryFn: () => api.get<Case>(`/cases/${caseId}`),
    enabled: !!caseId,
  });

  const stages = [
    {
      id: 'schema_design' as ProcessingStage,
      title: 'Schema Design',
      description: 'AI-driven graph schema generation',
      icon: Database,
    },
    {
      id: 'data_files' as ProcessingStage,
      title: 'Data Files',
      description: 'Upload source data files',
      icon: FileUp,
    },
    {
      id: 'data_mapping' as ProcessingStage,
      title: 'Data Mapping',
      description: 'Visualize & confirm mappings',
      icon: GitBranch,
    },
  ];

  const getStageStatus = (stageId: ProcessingStage): 'pending' | 'current' | 'completed' => {
    const stageIndex = stages.findIndex(s => s.id === stageId);
    const currentIndex = stages.findIndex(s => s.id === currentStage);

    if (stageIndex < currentIndex) return 'completed';
    if (stageIndex === currentIndex) return 'current';
    return 'pending';
  };

  const handleStageComplete = (stageId: ProcessingStage) => {
    const stageIndex = stages.findIndex(s => s.id === stageId);
    if (stageIndex < stages.length - 1) {
      setCurrentStage(stages[stageIndex + 1].id);
    } else {
      setCurrentStage('completed');
    }
  };

  if (isLoadingCase) {
    return (
      <div className="max-w-7xl mx-auto flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="max-w-7xl mx-auto">
        <Card>
          <CardBody>
            <p className="text-neutral-600">Case not found</p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
        <h1 className="text-[40px] font-bold text-neutral-900 mb-2">
          Processing Phase
        </h1>
        <p className="text-neutral-600">
          {caseData.name} - Generate schema and prepare data files
        </p>
      </div>

      {/* Progress Steps */}
      <Card className="mb-8">
        <CardBody>
          <div className="flex items-center justify-between">
            {stages.map((stage, index) => {
              const status = getStageStatus(stage.id);
              const Icon = stage.icon;

              return (
                <div key={stage.id} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    {/* Icon & Status */}
                    <div
                      className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${
                        status === 'completed'
                          ? 'bg-green-100'
                          : status === 'current'
                          ? 'bg-primary-100 ring-4 ring-primary-200'
                          : 'bg-neutral-100'
                      }`}
                    >
                      {status === 'completed' ? (
                        <CheckCircle2 className="w-8 h-8 text-green-600" />
                      ) : status === 'current' ? (
                        <Icon className="w-8 h-8 text-primary-600" />
                      ) : (
                        <Icon className="w-8 h-8 text-neutral-400" />
                      )}
                    </div>

                    {/* Title & Description */}
                    <h3
                      className={`text-sm font-semibold mb-1 text-center ${
                        status === 'current'
                          ? 'text-primary-700'
                          : status === 'completed'
                          ? 'text-green-700'
                          : 'text-neutral-500'
                      }`}
                    >
                      {stage.title}
                    </h3>
                    <p className="text-xs text-neutral-600 text-center max-w-[150px]">
                      {stage.description}
                    </p>
                  </div>

                  {/* Connector Line */}
                  {index < stages.length - 1 && (
                    <div className="flex-1 h-1 mx-4 relative" style={{ top: '-40px' }}>
                      <div
                        className={`h-full rounded ${
                          getStageStatus(stages[index + 1].id) === 'completed' ||
                          getStageStatus(stages[index + 1].id) === 'current'
                            ? 'bg-primary-500'
                            : 'bg-neutral-200'
                        }`}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>

      {/* Current Stage Content */}
      {currentStage === 'schema_design' && (
        <SchemaDesignStage
          caseId={caseId!}
          onComplete={() => handleStageComplete('schema_design')}
        />
      )}

      {currentStage === 'data_files' && (
        <DataFilesStage
          caseId={caseId!}
          onComplete={() => handleStageComplete('data_files')}
          onBack={() => setCurrentStage('schema_design')}
        />
      )}

      {currentStage === 'data_mapping' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-neutral-900">Data Mapping Visualization</h2>
                <p className="text-neutral-600 mt-1">
                  Review how source data maps to your Neo4j schema
                </p>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div style={{ height: '800px' }}>
              <MappingVisualization
                caseId={caseId!}
                onBack={() => setCurrentStage('data_files')}
              />
            </div>
          </CardBody>
        </Card>
      )}

      {currentStage === 'completed' && (
        <Card>
          <CardBody>
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                Processing Phase Complete!
              </h2>
              <p className="text-neutral-600 mb-6">
                Your graph schema has been designed and data files are ready for import.
              </p>
              <div className="flex items-center justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                >
                  Back to Dashboard
                </Button>
                <Button onClick={() => {
                  // TODO: Navigate to next phase (deployment/analytics)
                  navigate('/dashboard');
                }}>
                  Continue to Next Phase
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
